import React from 'react';
import BaseDialog from '../../../common/base-dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Alert, AlertTitle, Box, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../../contexts/strings';
import TextInputWithValidate from '../../../common/input/text-input-with-validate';
import SelectWithValidate from '../../../common/input/select-with-validate';
import { useFormValidate } from '../../../../hooks/formValidate';
import { Source, Segment } from '../../../../types/segment';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function SegmentForm(props: {
  segment: Segment;
  onSave: (formData: any | FormData, isFormData: boolean) => Promise<any>;
  onDelete: () => Promise<any>;
  onClose: () => void;
  isPending: boolean;
}) {
  const { segment, onSave, onClose, isPending, onDelete } = props;

  const { addFormValue, addFormValidation, isValid, isDirty } = useFormValidate();
  const [hideValidationError, setHideValidationError] = React.useState<boolean>(true);
  const [name, setName] = React.useState(segment.name || '');
  const [description, setDescription] = React.useState(segment.description || '');
  const [source, setSource] = React.useState(segment?.source || '');
  const [file, setFile] = React.useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { strings } = useStringsContext();
  const isNew = !segment?.id;
  const title = !isNew
    ? strings.pushNotifications.editSegment
    : strings.pushNotifications.newSegment;

  // Poll segments are created from a poll, so POLL is only shown for an existing poll segment
  const isPollSegment = segment?.source === Source.POLL;
  const options = isPollSegment
    ? [Source.POLL]
    : Object.values(Source).filter((option) => option !== Source.POLL);
  const hasUploadedFile = segment.id && segment?.source === Source.CSV;
  const showCSVSection = source === Source.CSV;
  const [allowForNewFile, setAllowForNewFile] = React.useState(!hasUploadedFile);
  const showCSVInput = !(file || !allowForNewFile);

  const isCSVInputDirty =
    showCSVSection && !showCSVInput && allowForNewFile ? Boolean(file) : false;
  const isFormDirty = isDirty || isCSVInputDirty;
  const isFormValid = isValid && showCSVSection && allowForNewFile ? Boolean(file) : true;

  return (
    <BaseDialog open title={title} onClose={onClose}>
      <DialogContent>
        {isNew && showCSVSection ? (
          <Alert sx={{ mt: 4 }} variant="outlined" color="info">
            <AlertTitle>Namnge gruppen tydligt</AlertTitle>
            Efter att du laddat upp din CSV-fil kan du inte se vilka användare som ingår i
            målgruppen. Ge därför målgruppen ett tydligt namn och en beskrivning som hjälper dig att
            känna igen den senare.
          </Alert>
        ) : null}
        <Box sx={{ pt: isNew && showCSVSection ? 4 : 7, pb: 2 }}>
          <Box sx={{ mb: 4 }}>
            <TextInputWithValidate
              value={name}
              name="name"
              onValue={(key, value) => {
                setName(value || '');
                addFormValue(key, value);
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              label={strings.pushNotifications.segmentsName}
              validation={yup.string().required(strings.validations.isRequired)}
              hideValidationError={hideValidationError}
            />
          </Box>
          <Box sx={{ mb: 4 }}>
            <TextInputWithValidate
              value={description}
              name="description"
              onValue={(key, value) => {
                setDescription(value || '');
                addFormValue(key, value);
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              label={strings.pushNotifications.segmentsDescription}
              validation={yup.string().required(strings.validations.isRequired)}
              hideValidationError={hideValidationError}
              multiline
              rows={5}
            />
          </Box>
          <Box sx={{ mb: 4 }}>
            <SelectWithValidate
              value={source}
              name="source"
              label={strings.pushNotifications.segmentsSource}
              onValue={(key, value) => {
                setSource(value || '');
                addFormValue(key, value);
              }}
              onValidation={(key, isValid) => {
                addFormValidation(key, isValid);
              }}
              options={options}
              optionsText={options.map((option) => {
                return strings.pushNotifications[`optionSegment${option}`];
              })}
              validation={yup.string().required(strings.validations.isRequired)}
              hideValidationError={hideValidationError}
              disabled={Boolean(segment.id)}
              fullWidth
            />
          </Box>
          {isPollSegment ? (
            <Alert color="info">
              {segment.idsCount} {strings.pushNotifications.users}
            </Alert>
          ) : null}
          {showCSVSection ? (
            <>
              {!showCSVInput ? (
                <Box>
                  <Alert
                    color="success"
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setFile(null);
                          setAllowForNewFile(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    {file ? (
                      <>{file.name}</>
                    ) : (
                      <>
                        {segment.idsCount} {strings.pushNotifications.users}
                      </>
                    )}
                  </Alert>
                </Box>
              ) : (
                <>
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    fullWidth
                  >
                    {strings.pushNotifications.selectSourceFile}
                    <VisuallyHiddenInput
                      id="csv-input"
                      type="file"
                      accept=".csv"
                      onChange={(event) => {
                        if (event.target.files?.[0]) {
                          setFile(event.target.files[0]);
                        }
                      }}
                    />
                  </Button>
                  <Typography variant="caption" color="error">
                    {!hideValidationError ? strings.validations.isRequired : null}
                  </Typography>
                </>
              )}
            </>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={(theme) => ({ borderTop: `1px solid ${theme.palette.grey[300]}` })}>
        <Stack direction="row" gap={2} sx={{ flex: 1 }}>
          {segment.id ? (
            <Button
              color="error"
              variant="text"
              onClick={async () => {
                onDelete();
                onClose();
              }}
            >
              {strings.pushNotifications.deleteButton}
            </Button>
          ) : null}
        </Stack>
        <Stack direction="row" gap={2}>
          <Button
            color="primary"
            onClick={() => {
              onClose();
            }}
          >
            {strings.actions.cancel}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!isFormDirty || !isFormValid) {
                setHideValidationError(false);
                return;
              }
              try {
                const fileData = new FormData();
                fileData.append('name', name);
                fileData.append('description', description);
                fileData.append('source', source);
                if (allowForNewFile && file && source === Source.CSV) {
                  fileData.append('csvFile', file);
                }
                await onSave(fileData, true);
                setFile(null);
                setAllowForNewFile(false);
                const message = strings.messages.segmentSuccess;
                enqueueSnackbar(message, {
                  variant: 'success',
                });
                onClose();
              } catch (error: any) {
                console.error(error);

                enqueueSnackbar(strings.messages.saveError, {
                  variant: 'error',
                });
              }
            }}
            disabled={!isFormDirty}
            loading={isPending}
          >
            {strings.pushNotifications.saveSegment}
          </Button>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
}
